import { asArray, getStatementEndDate, isDateGreater } from './promoPrimitives';
import { getOfferInfo } from './promoModel';
import { rawLevels } from './promoPhase';
import { hasValidOfferDates, localOfferHasBlockingError, localProductUCError } from './promoChecks';
import { isRatedOnNego } from './promoForecast';

// Status bars of the campaigns, offers and products (#23521, spec RG-07 / RG-08) : green when filled for the phase,
// orange when partial, red when nothing. Port of `sfaHelper.campaign.getPromoStatusColor` (product level, the only one
// still configured) and of the step overrides `getPromoStatusColor` / `getLocalePromoStatusColor` (JS:974-1065).
// Values are Vuetify theme colors : 'success' | 'warning' | 'error' | ''.

export const STATUS = Object.freeze({ SUCCESS: 'success', WARNING: 'warning', ERROR: 'error', NONE: '' });

const toStatus = (nbOK, nbKO) => {
	if (nbOK > 0 && nbKO === 0) return STATUS.SUCCESS;
	if (nbOK > 0 && nbKO > 0) return STATUS.WARNING;
	if (nbOK === 0 && nbKO > 0) return STATUS.ERROR;
	return STATUS.NONE;
};

const inAssort = (snapshot, productId) => !snapshot?.assortFilterActive || !!snapshot?.products?.[productId]?.inAssortFilter;

const uniqueIds = (ids) => [...new Set(ids)];

/** Offers a campaign aggregates : published with visible products ; in the statement, a national offer must still be visible (#23445). */
const aggregatedOffers = (campaign, forStatement) => asArray(campaign?.offers).filter((offer) => offer.hasPublishedProducts && offer.hasVisibleProducts && (!forStatement || campaign.promoLocal || offer.visible !== false));

const aggregate = (results) => {
	if (!results.length || results.includes('red')) return { nbOK: 0, nbKO: 1 };
	if (results.includes('orange')) return { nbOK: 1, nbKO: 1 };
	return { nbOK: 1, nbKO: 0 };
};

function negoStatus(snapshot, campaign, offerId, productId, offerInfo, forFreeRow) {
	const isLocal = campaign.promoLocal;
	const negos = isLocal && offerInfo ? asArray(offerInfo._negociatedCampaignOffers) : asArray(snapshot?.model?.negotiated);
	const matches = (row) => !row.additional && !row.isFree && (!offerId || row.campaignOfferId === offerId) && (!productId || row.productId === productId);

	let res = negos.filter((row) => matches(row) && row.campaignId === campaign.id);
	if (isLocal) {
		const source = offerInfo
			? negos
			: asArray(snapshot?.model?.offerInfos)
					.filter((oi) => oi.campaignId === campaign.id && isDateGreater(oi.endDate, snapshot.now, true))
					.flatMap((oi) => asArray(oi._negociatedCampaignOffers));
		res = source.filter(matches);
	}
	let nbOK = res.filter((row) => row.volume > 0 && inAssort(snapshot, row.productId)).length;
	let nbKO = 0;

	if (!offerId && !productId) {
		const results = aggregatedOffers(campaign, false)
			.map((offer) => {
				const offerRes = negos.filter((row) => !row.additional && !row.isFree && row.campaignId === campaign.id && row.campaignOfferId === offer.id);
				const productIds = uniqueIds(
					asArray(offer.products)
						.filter((p) => p.published)
						.map((p) => p.productId)
				).filter((id) => inAssort(snapshot, id));
				if (!productIds.length) return null;
				const offerNbOK = offerRes.filter((row) => row.volume > 0 && inAssort(snapshot, row.productId)).length;
				if (offerNbOK === productIds.length) return 'green';
				return offerNbOK > 0 ? 'orange' : 'red';
			})
			.filter(Boolean);
		({ nbOK, nbKO } = aggregate(results));
	}

	if (offerId && !productId) {
		const offerRes = negos.filter((row) => !row.additional && !row.isFree && row.campaignId === campaign.id && row.campaignOfferId === offerId);
		let nbProducts = uniqueIds(
			asArray(campaign.offers)
				.filter((o) => o.id === offerId)
				.flatMap((o) => asArray(o.products))
				.map((p) => p.productId)
		).filter((id) => inAssort(snapshot, id)).length;
		if (isLocal) nbProducts = offerRes.length;
		nbOK = offerRes.filter((row) => row.volume > 0 && inAssort(snapshot, row.productId)).length;
		if (nbOK !== nbProducts) nbKO++;
	}

	if (offerId && productId) {
		const productRes = negos.filter((row) => !row.additional && (forFreeRow ? row.isFree : !row.isFree) && row.campaignId === campaign.id && row.campaignOfferId === offerId && row.productId === productId);
		nbOK = productRes.filter((row) => row.volume > 0).length;
		if (nbOK === 0) nbKO++;
	}

	return toStatus(nbOK, nbKO);
}

/** Free lines negotiated on an offer : each one is a line to state, next to the paying lines (#23572). */
const negotiatedFreeIds = (negos, campaignId, offerId) =>
	uniqueIds(
		asArray(negos)
			.filter((row) => !row.additional && row.isFree && row.volume > 0 && row.campaignId === campaignId && row.campaignOfferId === offerId)
			.map((row) => row.productId)
	);

/** Lines stated among the lines to state of an offer : its paying products `payingIds` and its free lines `freeIds`. */
const statedLinesCount = (offerStated, payingIds, freeIds) => offerStated.filter((row) => row.present > 0 && (row.isFree ? freeIds : payingIds).includes(row.productId)).length;

function storeStatus(snapshot, campaign, offerId, productId, offerInfo, forFreeRow) {
	const isLocal = campaign.promoLocal;
	const stated = isLocal && offerInfo ? asArray(offerInfo._statedCampaignOffers) : asArray(snapshot?.model?.stated);
	const negos = isLocal && offerInfo ? asArray(offerInfo._negociatedCampaignOffers) : asArray(snapshot?.model?.negotiated);
	const matches = (row) => !row.additional && row.campaignId === campaign.id && (!offerId || row.campaignOfferId === offerId) && (!productId || (row.productId === productId && !!row.isFree === !!forFreeRow));

	let res = stated.filter(matches);
	if (isLocal) {
		const source = offerInfo
			? stated
			: asArray(snapshot?.model?.offerInfos)
					.filter((oi) => oi.campaignId === campaign.id && isDateGreater(getStatementEndDate(oi.endDate, oi), snapshot.now, true))
					.flatMap((oi) => asArray(oi._statedCampaignOffers));
		res = source.filter(matches);
	}
	let nbOK = res.filter((row) => row.present > 0).length;
	let nbKO = res.filter((row) => row.present === 0).length;

	if (!offerId && !productId) {
		const results = aggregatedOffers(campaign, true)
			.map((offer) => {
				const offerStated = stated.filter((row) => !row.additional && row.campaignId === campaign.id && row.campaignOfferId === offer.id);
				const productIds = uniqueIds(
					asArray(offer.products)
						.filter((p) => p.published)
						.map((p) => p.productId)
				);
				if (!productIds.length) return null;
				const freeIds = negotiatedFreeIds(negos, campaign.id, offer.id);
				const offerNbOK = statedLinesCount(offerStated, productIds, freeIds);
				if (offerNbOK === productIds.length + freeIds.length) return 'green';
				return offerNbOK > 0 ? 'orange' : 'red';
			})
			.filter(Boolean);
		({ nbOK, nbKO } = aggregate(results));
	}

	if (offerId && !productId) {
		const offerRes = stated.filter((row) => !row.additional && row.campaignId === campaign.id && row.campaignOfferId === offerId);
		let payingIds = uniqueIds(
			asArray(campaign.offers)
				.filter((o) => o.id === offerId)
				.flatMap((o) => asArray(o.products))
				.map((p) => p.productId)
		);
		if (isLocal && offerInfo) payingIds = uniqueIds(negos.filter((row) => !row.additional && !row.isFree && row.campaignId === campaign.id && row.campaignOfferId === offerId && row.volume > 0).map((row) => row.productId));
		const freeIds = negotiatedFreeIds(negos, campaign.id, offerId);
		nbOK = statedLinesCount(offerRes, payingIds, freeIds);
		if (nbOK !== payingIds.length + freeIds.length) nbKO++;
	}

	if (offerId && productId) {
		if (nbOK !== 1) nbKO++;
	}

	return toStatus(nbOK, nbKO);
}

/**
 * `sfaHelper.campaign.getPromoStatusColor` : nego period first, then store period, on the raw (date-based) levels. A local
 * offer kept in the negotiation display by its forecast (RG-24) is rated on its negotiation.
 */
export function legacyStatus(snapshot, campaign, offerId, productId, offerInfo = null, forFreeRow = false) {
	if (!campaign) return STATUS.NONE;
	if (isRatedOnNego(snapshot, campaign, offerInfo)) return negoStatus(snapshot, campaign, offerId, productId, offerInfo, forFreeRow);
	if (rawLevels(snapshot, campaign, campaign.promoLocal && offerInfo ? offerInfo._key : null).store > 0) return storeStatus(snapshot, campaign, offerId, productId, offerInfo, forFreeRow);
	return STATUS.NONE;
}

/** Blocking error of a local offer, as the validation raises it : its nego rules only while it is negotiated (#23572). */
const localBlockingError = (snapshot, campaign, offerInfo) => (isRatedOnNego(snapshot, campaign, offerInfo) ? localOfferHasBlockingError(campaign, offerInfo) : !hasValidOfferDates(offerInfo));

const hasPayingVolume = (rows) => asArray(rows).some((row) => !row.additional && row.volume > 0);

/** Status of a local campaign : every offer still running, red on any blocking error (TFS#20924). */
function localCampaignStatus(snapshot, campaign) {
	const offerInfos = asArray(snapshot?.model?.offerInfos).filter((oi) => oi.campaignId === campaign.id && oi.campaignOfferId > 0 && (isDateGreater(getStatementEndDate(oi.endDate, oi), snapshot.now, true) || !hasValidOfferDates(oi)));
	if (offerInfos.some((oi) => localBlockingError(snapshot, campaign, oi))) return STATUS.ERROR;
	const statuses = offerInfos.map((oi) => {
		const status = legacyStatus(snapshot, campaign, oi.campaignOfferId, undefined, oi);
		return status === STATUS.ERROR && isRatedOnNego(snapshot, campaign, oi) && hasPayingVolume(oi._negociatedCampaignOffers) ? STATUS.WARNING : status;
	});
	if (!statuses.length) return STATUS.NONE;
	if (statuses.includes(STATUS.ERROR)) return STATUS.ERROR;
	if (statuses.includes(STATUS.WARNING)) return STATUS.WARNING;
	if (statuses.includes(STATUS.SUCCESS)) return STATUS.SUCCESS;
	return STATUS.NONE;
}

/**
 * Campaign bar. A red campaign with at least one negotiated product turns orange during the negotiation only : in the
 * statement, the bar rates what was stated (RG-10), so nothing stated stays red.
 */
export function campaignStatus(snapshot, campaign) {
	if (!campaign) return STATUS.NONE;
	if (campaign.promoLocal) return localCampaignStatus(snapshot, campaign);
	const status = legacyStatus(snapshot, campaign);
	if (status === STATUS.ERROR && isRatedOnNego(snapshot, campaign) && asArray(snapshot?.model?.negotiated).some((row) => !row.additional && row.volume > 0 && row.campaignId === campaign.id)) return STATUS.WARNING;
	return status;
}

/** Offer bar : during the negotiation, a red offer with at least one negotiated product turns orange, unless a local offer has a blocking error. */
export function offerStatus(snapshot, campaign, offerId, offerKey = null) {
	if (!campaign) return STATUS.NONE;
	if (campaign.promoLocal) {
		const offerInfo = getOfferInfo(snapshot, offerKey);
		if (localBlockingError(snapshot, campaign, offerInfo)) return STATUS.ERROR;
		const status = legacyStatus(snapshot, campaign, offerInfo?.campaignOfferId, undefined, offerInfo);
		if (status === STATUS.ERROR && offerInfo && isRatedOnNego(snapshot, campaign, offerInfo) && hasPayingVolume(offerInfo._negociatedCampaignOffers)) return STATUS.WARNING;
		return status;
	}
	const status = legacyStatus(snapshot, campaign, offerId);
	if (status === STATUS.ERROR && isRatedOnNego(snapshot, campaign) && asArray(snapshot?.model?.negotiated).some((row) => !row.additional && row.volume > 0 && row.campaignId === campaign.id && row.campaignOfferId === offerId)) return STATUS.WARNING;
	return status;
}

/** Product bar (paying row, or the free row with `forFreeRow`). */
export function productStatus(snapshot, campaign, offerId, productId, offerKey = null, forFreeRow = false) {
	if (!campaign) return STATUS.NONE;
	if (campaign.promoLocal) {
		const offerInfo = getOfferInfo(snapshot, offerKey);
		if (!forFreeRow && isRatedOnNego(snapshot, campaign, offerInfo) && localProductUCError(campaign, offerInfo, productId)) return STATUS.ERROR;
		return legacyStatus(snapshot, campaign, offerInfo?.campaignOfferId, productId, offerInfo, forFreeRow);
	}
	return legacyStatus(snapshot, campaign, offerId, productId, null, forFreeRow);
}
